import React, { useEffect, useRef, useState } from "react";

import { apiFetch } from "./api";

function Leads({ globalFilter, setGlobalFilter, setPage }) {
  const [leads, setLeads] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [requirement, setRequirement] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [noteInputs, setNoteInputs] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [excelFile, setExcelFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [noteTypeInputs, setNoteTypeInputs] = useState({});
  const [localNames, setLocalNames] = useState({});
  const [workspaceMessages, setWorkspaceMessages] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const timelineRef = useRef(null);
  const [selectedTimelineLead, setSelectedTimelineLead] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [selectedLeadForNote, setSelectedLeadForNote] = useState(null);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [addCustomerMode, setAddCustomerMode] = useState(null);

  useEffect(() => {
    if (timelineRef.current) timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [workspaceMessages, selectedTimelineLead?._id]);

  const importExcel = async () => {
    if (!excelFile) {
      alert("Please select an Excel file to import.");
      return;
    }
  
    try {
      setImporting(true);
  
      const formData = new FormData();
      formData.append("file", excelFile);
      
  
      const res = await apiFetch("/import-excel", {
        method: "POST",
        body: formData
      });
  
      const data = await res.json();
  
      // Handle backend errors
      if (!res.ok) {
        alert(
          "❌ Invalid Excel Template\n\n" +
          "The uploaded file doesn't match the required Mukbiz format.\n\n" +
          "Please click 'Download Template', delete the sample rows, add your customer details, and import the file again."
        );
        return;
      }
  
      // Success
      alert(
        "✅ Import Completed Successfully\n\n" +
        `Imported : ${data.imported} customer(s)\n` +
        `Skipped : ${data.skipped} duplicate customer(s)`
      );
  
      setExcelFile(null);
  
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";
  
      await fetchLeads();
  
    } catch (err) {
      console.error(err);
  
      alert(
        "❌ Import Failed\n\n" +
        "Something went wrong while importing your customers.\n" +
        "Please try again."
      );
    } finally {
      setImporting(false);
    }
  };
  
  const addLead = async () => {
    setError("");
  
    // Validation first
    if (!name || !phone || !requirement) {
      setError("All fields are required");
      return;
    }
  
    if (phone.length !== 10 || isNaN(phone)) {
      setError("Please enter only the 10-digit mobile number (without +91 or leading 0)");
      return;
    }
  
    setSaving(true);
  
    try {
      const res = await apiFetch("/leads", {
        method: "POST",
        body: JSON.stringify({
          name,
          phone,
          requirement
        })
      });
  
      const data = await res.json();
  
      alert(data.message);
  
      // Clear form
      setName("");
      setPhone("");
      setRequirement("");
  
      // Refresh customer list
      await fetchLeads();
  
    } catch (err) {
      console.error("Error adding lead:", err);
      setError("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const loadWhatsAppMessages = async (leadId) => {
    try {
      console.log(
        "Loading WhatsApp messages for Lead:",
        leadId
      );
  
      const res = await apiFetch(
        `/whatsapp/messages/lead/${leadId}`
      );
  
      const data = await res.json();
  
      if (!res.ok) {
        throw new Error(
          data.error ||
          "Failed to load WhatsApp messages"
        );
      }
  
      console.log(
        "WhatsApp messages loaded:",
        data.messages
      );
  
  
      return data.messages || [];
  
    } catch (error) {
  
      console.error(
        "Error loading WhatsApp messages:",
        error
      );
  
      
  
      return [];
    }
  };

  const openCustomerTimeline = async (lead) => {
    setSelectedTimelineLead(lead);
    setReplyText("");
    setLoadingConversation(true);
    try {
      const messages = await loadWhatsAppMessages(lead._id);
      setWorkspaceMessages(messages);
    } finally {
      setLoadingConversation(false);
    }
  };

  // Refresh the active conversation, including WhatsApp Business app echoes.
  useEffect(() => {
    if (!selectedTimelineLead?._id) return;
    let active = true;
    const refresh = async () => {
      if (document.hidden) return;
      const messages = await loadWhatsAppMessages(selectedTimelineLead._id);
      if (active) setWorkspaceMessages(messages);
    };
    const interval = setInterval(refresh, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [selectedTimelineLead?._id]);

  const sendWhatsAppReply = async () => {

    if (!selectedTimelineLead) {
      return;
    }
  
    if (!replyText.trim()) {
      return;
    }
  
    setSendingReply(true);
  
    try {
  
      const res = await apiFetch(
        "/whatsapp/send-reply",
        {
          method: "POST",
          body: JSON.stringify({
            leadId: selectedTimelineLead._id,
            message: replyText.trim()
          })
        }
      );
  
      const data = await res.json();
  
      if (!res.ok) {
        throw new Error(
          data.error ||
          "Failed to send WhatsApp reply"
        );
      }
  
      console.log(
        "WhatsApp reply sent:",
        data
      );
  
      setReplyText("");
  
      const messages = await loadWhatsAppMessages(selectedTimelineLead._id);
      setWorkspaceMessages(messages);

      await fetchLeads();
  
    } catch (error) {
  
      console.error(
        "WhatsApp reply error:",
        error
      );
  
      alert(
        error.message ||
        "Failed to send WhatsApp reply"
      );
  
    } finally {
  
      setSendingReply(false);
  
    }
  };


  const updateFollowup = (id, next_followup_date) => {
    apiFetch(`/lead/${id}`, {
      method: "PUT",
      body: JSON.stringify({ next_followup_date })
    })
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to update follow-up");
        }
        return res.json();
      })
      .then(() => fetchLeads())
      .catch(err => {
        console.error("Error updating follow-up:", err);
      });
  };


  const fetchLeads = async () => {
    try {
      const res = await apiFetch("/leads");
  
      if (!res.ok) {
        throw new Error("Failed to fetch leads");
      }
  
      const data = await res.json();
      setLeads(data);
  
    } catch (err) {
      console.error("Error fetching leads:", err);
    }
  };

  useEffect(() => {
    fetchLeads();
  
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchLeads();
      }
    }, 15000);
  
    return () => clearInterval(interval);
  }, []);

  // 🔥 Update status function
  const updateStatus = (id, status) => {
    apiFetch(`/lead/${id}`, {
      method: "PUT",
      body: JSON.stringify({ status })
    })
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to update status");
        }
        return res.json();
      })
      .then(data => {
        alert(data.message);   // 👈 conflict or success message
        fetchLeads();          // refresh list
      })
      .catch(err => {
        console.error("Error updating status:", err);
        alert("Something went wrong");
      });
  };
  const getFilterLabel = () => {
    if (globalFilter?.leadId) {
      return "FOCUS_MODE";
    }
    if (globalFilter) {
      
      if (globalFilter.type === "overdue") return "Overdue Leads";
      if (globalFilter.type === "today") return "Today’s Follow-ups";
      if (globalFilter.status) return `${globalFilter.status} Leads`;
      if (globalFilter.type === "all") return "All Leads";
    }
  
    if (statusFilter !== "All") {
      return `${statusFilter} Leads`;
    }
  
    if (search) {
      return `Search: "${search}"`;
    }
  
    return null;
  };
  
  const addNote = (id) => {
    const note = noteInputs[id];
    const type = noteTypeInputs[id] || "User";
  
    if (!note || note.trim() === "") {
      alert("Please enter a note");
      return;
    }
  
    apiFetch(`/lead/${id}/note`, {
      method: "POST",
      
      body: JSON.stringify({ note, type })
    })
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to add note");
        }
        return res.json();
      })
      .then(() => {
        setNoteInputs(prev => ({
          ...prev,
          [id]: ""
        }));
        fetchLeads();
      })
      .catch(err => {
        console.error("Error adding note:", err);
        alert("Something went wrong");
      });
  };
  const updateLeadField = (id, field, value) => {
    apiFetch(`/lead/${id}`, {
      method: "PUT",
      body: JSON.stringify({ [field]: value })
    })
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to update field");
        }
        return res.json();
      })
      .then(() => fetchLeads())
      .catch(err => {
        console.error("Error updating field:", err);
      });
  };

  const getStatusColor = (lead) => {
    switch (lead.status) {
      case "New":
        return "#3b82f6";
  
      case "In Progress":
        return "#f59e0b";
  
      case "Won":
        return "#10b981";
  
      case "Lost":
        return "#ef4444";
  
      case "Imported":
        return "#7c3aed";
  
      default:
        return "#9ca3af";
    }
  };

  const label = getFilterLabel();

  const filteredLeads = leads.filter((lead) => {
    const today = new Date().toLocaleDateString("en-CA");
    const todayDate = new Date(today);
  
    if (globalFilter) {
      if (globalFilter.leadId && lead._id !== globalFilter.leadId)
        return false;

      if (
        globalFilter.type === "today" &&
        (
          lead.status === "Won" ||
          lead.status === "Lost" ||
          lead.next_followup_date !== today
        )
      )
        return false;
  
      if (
        globalFilter.type === "overdue" &&
        (
          !lead.next_followup_date ||
          lead.status === "Won" ||
          lead.status === "Lost" ||
          new Date(lead.next_followup_date) >= todayDate
        )
      )
        return false;
      if (globalFilter.status && lead.status !== globalFilter.status) return false;
    }
  
    const query = search.toLowerCase();

    const matchesSearch =
      (lead.name || "").toLowerCase().includes(query) ||
      (lead.phone || "").toLowerCase().includes(query) ||
      (lead.requirement || "").toLowerCase().includes(query) ||
      (lead.notes || []).some(note =>
        (note.text || "").toLowerCase().includes(query)
      );
  
    const matchesStatus =
      statusFilter === "All" || lead.status === statusFilter;
  
    return matchesSearch && matchesStatus;
  });

  return (
    <div style={{ padding: 20 }}>
  
      {/* PAGE HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20
        }}
      >
        <h2
          style={{
            color: "#020079",
            margin: 0
          }}
        >
          Customers
        </h2>
  
        <button
          onClick={() => {
            setGlobalFilter(null);
            setPage("dashboard");
          }}
          style={{
            background: "none",
            border: "none",
            color: "#2563eb",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            padding: "6px 0"
          }}
        >
          ← Back to Dashboard
        </button>
      </div>
  
      {/* ADD CUSTOMER SECTION */}
      <div
        style={{
          marginBottom: 20,
          padding: "16px 18px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "10px"
        }}
      >
        
  
        <button
          onClick={() => {
            setAddCustomerMode(null);
            setShowAddCustomerModal(true);
          }}
          style={{
            padding: "9px 16px",
            background: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "7px",
            cursor: "pointer",
            fontSize: "13px",
            fontWeight: "600"
          }}
        >
          ＋ Add Customer
        </button>
      </div>
  
  
      {/* FIND CUSTOMERS SECTION */}
      <div
        style={{
          marginBottom: 20,
          padding: "16px 18px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "10px"
        }}
      >
        <div
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: "#1f2937",
            marginBottom: "10px"
          }}
        >
          Find Customers
        </div>
  
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap"
          }}
        >
          <input
            placeholder="Search Name, Phone or Requirement or Notes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "300px",
              padding: "8px 10px",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              boxSizing: "border-box"
            }}
          />
  
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
  
              if (setGlobalFilter) {
                setGlobalFilter(null);
              }
            }}
            style={{
              padding: "8px 10px",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              background: "#ffffff"
            }}
          >
            <option value="All">All</option>
            <option value="New">New</option>
            <option value="Imported">Imported</option>
            <option value="In Progress">In Progress</option>
            <option value="Won">Won</option>
            <option value="Lost">Lost</option>
          </select>
        </div>
      </div>
  
  
      {/* DASHBOARD FILTER / SEARCH RESULT BANNER */}
      {label && (
        <div
          style={{
            marginBottom: 20,
            padding: "14px 18px",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div>
  
            {label === "FOCUS_MODE" ? (
              <>
                <div
                  style={{
                    fontWeight: "700",
                    color: "#1d4ed8",
                    fontSize: "16px"
                  }}
                >
                  🎯 Focus Mode
                </div>
  
                <div
                  style={{
                    marginTop: 4,
                    color: "#475569",
                    fontSize: "14px"
                  }}
                >
                  Showing your selected priority customer.
                </div>
              </>
            ) : (
              <strong>
                Showing:{" "}
                {label
                  .replace(/ Leads/g, "")
                  .replace(/ Customers/g, "")}
              </strong>
            )}
  
          </div>
  
          <button
            onClick={() => {
              setGlobalFilter(null);
              setStatusFilter("All");
              setSearch("");
            }}
            style={{
              padding: "8px 14px",
              background: "#ffffff",
              color: "#2563eb",
              border: "1px solid #93c5fd",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              transition: "0.2s"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = "#2563eb";
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = "#ffffff";
              e.currentTarget.style.color = "#2563eb";
            }}
          >
            View All Customers
          </button>
        </div>
      )}
  
  
      {/* ADD CUSTOMER MODAL */}
      {showAddCustomerModal && (
        <div
          onClick={() => setShowAddCustomerModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "20px",
            boxSizing: "border-box"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              width: "90%",
              maxWidth: "520px",
              padding: "24px",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              position: "relative",
              boxSizing: "border-box"
            }}
          >
  
            {/* CLOSE */}
            <button
              onClick={() => setShowAddCustomerModal(false)}
              title="Close"
              aria-label="Close"
              style={{
                position: "absolute",
                top: "10px",
                right: "12px",
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                border: "none",
                background: "#f3f4f6",
                color: "#374151",
                fontSize: "20px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: "1"
              }}
            >
              ×
            </button>
  
  
            {/* MODAL TITLE */}
            <h2
              style={{
                marginTop: 0,
                marginBottom: 6,
                color: "#1f2937"
              }}
            >
              Add Customer
            </h2>
  
  
            {/* SELECTION SCREEN */}
            {!addCustomerMode && (
              <>
                <div
                  style={{
                    color: "#64748b",
                    fontSize: "14px",
                    marginBottom: "20px"
                  }}
                >
                  How would you like to add the customer?
                </div>
  
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px"
                  }}
                >
  
                  {/* MANUAL */}
                  <button
                    onClick={() => setAddCustomerMode("manual")}
                    style={{
                      width: "100%",
                      padding: "14px",
                      background: "#2563eb",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                      textAlign: "left"
                    }}
                  >
                    ✍️ Add Customer Manually
                    <div
                      style={{
                        marginTop: "4px",
                        fontSize: "12px",
                        fontWeight: "400",
                        opacity: 0.9
                      }}
                    >
                      Enter one customer's details
                    </div>
                  </button>
  
  
                  {/* EXCEL */}
                  <button
                    onClick={() => setAddCustomerMode("excel")}
                    style={{
                      width: "100%",
                      padding: "14px",
                      background: "#16a34a",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: "600",
                      fontSize: "14px",
                      textAlign: "left"
                    }}
                  >
                    📥 Import Customers from Excel
                    <div
                      style={{
                        marginTop: "4px",
                        fontSize: "12px",
                        fontWeight: "400",
                        opacity: 0.9
                      }}
                    >
                      Add multiple customers at once
                    </div>
                  </button>
  
                </div>
              </>
            )}
  
  
            {/* MANUAL CUSTOMER FORM */}
            {addCustomerMode === "manual" && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px"
                  }}
                >
                  <strong>Add Customer Manually</strong>
  
                  <button
                    onClick={() => setAddCustomerMode(null)}
                    style={{
                      border: "none",
                      background: "none",
                      color: "#2563eb",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    ← Back
                  </button>
                </div>
  
  
                <input
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px",
                    marginBottom: "10px",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px"
                  }}
                />
  
                <input
                  placeholder="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px",
                    marginBottom: "10px",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px"
                  }}
                />
  
                <input
                  placeholder="Requirement"
                  value={requirement}
                  onChange={(e) => setRequirement(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px",
                    marginBottom: "15px",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px"
                  }}
                />
  
                {error && (
                  <div
                    style={{
                      color: "red",
                      marginBottom: 10,
                      fontSize: "13px"
                    }}
                  >
                    {error}
                  </div>
                )}
  
                <button
                  onClick={async () => {
                    await addLead();
                  }}
                  disabled={!name || !phone || !requirement || saving}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "7px",
                    cursor:
                      !name || !phone || !requirement || saving
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      !name || !phone || !requirement || saving
                        ? 0.5
                        : 1,
                    fontWeight: "600"
                  }}
                >
                  {saving ? "Saving..." : "Add Customer"}
                </button>
              </>
            )}
  
  
            {/* EXCEL IMPORT */}
            {addCustomerMode === "excel" && (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px"
                  }}
                >
                  <strong>Import Customers from Excel</strong>
  
                  <button
                    onClick={() => setAddCustomerMode(null)}
                    style={{
                      border: "none",
                      background: "none",
                      color: "#2563eb",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    ← Back
                  </button>
                </div>
  
  
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => setExcelFile(e.target.files[0])}
                  style={{
                    width: "100%",
                    marginBottom: "15px"
                  }}
                />
  
                {error && (
                  <div
                    style={{
                      color: "red",
                      marginBottom: 10,
                      fontSize: "13px"
                    }}
                  >
                    {error}
                  </div>
                )}
  
                <button
                  onClick={async () => {
                    await importExcel();
                  }}
                  disabled={!excelFile || importing}
                  style={{
                    width: "100%",
                    padding: "10px",
                    background: "#2563eb",
                    color: "white",
                    border: "none",
                    borderRadius: "7px",
                    cursor:
                      !excelFile || importing
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      !excelFile || importing
                        ? 0.5
                        : 1,
                    fontWeight: "600"
                  }}
                >
                  {importing
                    ? "Importing..."
                    : "📥 Import Excel"}
                </button>
  
  
                <a
                  href="/Mukbiz_Customer_Template.xlsx"
                  download
                  style={{
                    display: "block",
                    marginTop: "10px",
                    textAlign: "center",
                    padding: "9px",
                    background: "#16a34a",
                    color: "white",
                    borderRadius: "7px",
                    textDecoration: "none",
                    fontWeight: "600",
                    fontSize: "13px"
                  }}
                >
                  ⬇ Download Customer Template
                </a>
  
              </>
            )}
  
          </div>
        </div>
      )}
  
  
      {/* THREE-PANEL MUKBI WORKSPACE */}
      <style>{`
        .mukbi-workspace { display:grid; grid-template-columns:minmax(230px, 28%) minmax(320px, 1fr) minmax(270px, 29%); gap:12px; min-height:620px; }
        .mukbi-panel { background:white; border:1px solid #e2e8f0; border-radius:12px; min-width:0; overflow:hidden; display:flex; flex-direction:column; }
        .mukbi-scroll { overflow-y:auto; max-height:570px; }
        .mukbi-customer:hover { background:#eff6ff !important; }
        @media(max-width:1100px) { .mukbi-workspace { grid-template-columns: minmax(210px, 35%) minmax(300px,1fr); } .mukbi-details { grid-column:1 / -1; } }
        @media(max-width:680px) { .mukbi-workspace { display:flex; flex-direction:column; } .mukbi-scroll { max-height:320px; } .mukbi-conversation { min-height:460px; } }
      `}</style>
      <div className="mukbi-workspace">
        <section className="mukbi-panel" aria-label="Customers">
          <div style={{padding:16,borderBottom:"1px solid #e2e8f0",fontWeight:700,color:"#0f172a"}}>Customers ({filteredLeads.length})</div>
          <div className="mukbi-scroll">
            {filteredLeads.map(lead => (
              <button key={lead._id} className="mukbi-customer" onClick={() => openCustomerTimeline(lead)}
                style={{width:"100%",display:"block",textAlign:"left",padding:"14px 16px",border:"none",borderBottom:"1px solid #f1f5f9",borderLeft:`4px solid ${getStatusColor(lead)}`,background:selectedTimelineLead?._id===lead._id?"#eff6ff":"white",cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"center"}}>
                  <strong style={{color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.name || "Unnamed customer"}</strong>
                  <span style={{fontSize:10,color:getStatusColor(lead),flexShrink:0}}>{lead.status || "New"}</span>
                </div>
                <div style={{fontSize:12,color:"#64748b",marginTop:5}}>{lead.phone || "No phone"}</div>
                <div style={{fontSize:12,color:"#475569",marginTop:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.requirement || "No requirement"}</div>
              </button>
            ))}
            {filteredLeads.length === 0 && <p style={{padding:16,color:"#64748b"}}>No matching customers.</p>}
          </div>
        </section>

        <section className="mukbi-panel mukbi-conversation" aria-label="WhatsApp conversation">
          {selectedTimelineLead ? (
            <>
              <div style={{padding:16,borderBottom:"1px solid #e2e8f0"}}>
                <strong style={{color:"#0f172a"}}>{(leads.find(l=>l._id===selectedTimelineLead._id) || selectedTimelineLead).name}</strong>
                <div style={{fontSize:12,color:"#64748b",marginTop:3}}>WhatsApp · {selectedTimelineLead.phone}</div>
              </div>
              <div ref={timelineRef} style={{flex:1,minHeight:300,maxHeight:490,overflowY:"auto",padding:16,background:"#f8fafc"}}>
                {loadingConversation && <p style={{color:"#64748b"}}>Loading messages…</p>}
                {!loadingConversation && workspaceMessages.length===0 && <p style={{color:"#64748b",textAlign:"center"}}>No WhatsApp messages yet.</p>}
                {[...workspaceMessages].sort((a,b)=>new Date(a.whatsappTimestamp||a.created_at||0)-new Date(b.whatsappTimestamp||b.created_at||0)).map((message,index)=>{
                  const outbound=message.direction==="outbound";
                  const stamp=message.whatsappTimestamp || message.created_at;
                  return <div key={message._id || message.waMessageId || index} style={{display:"flex",justifyContent:outbound?"flex-end":"flex-start",marginBottom:12}}>
                    <div style={{maxWidth:"82%",padding:"10px 13px",borderRadius:12,background:outbound?"#dcfce7":"white",border:"1px solid #e2e8f0",overflowWrap:"anywhere"}}>
                      <div style={{fontSize:14,color:"#1f2937",whiteSpace:"pre-wrap"}}>{message.text || `[${message.messageType || "Message"}]`}</div>
                      <div style={{fontSize:10,color:"#64748b",marginTop:6,textAlign:"right"}}>{stamp && !Number.isNaN(new Date(stamp).getTime()) ? new Date(stamp).toLocaleString("en-IN") : ""}</div>
                    </div>
                  </div>;
                })}
              </div>
              <div style={{padding:12,borderTop:"1px solid #e2e8f0",display:"flex",gap:8,alignItems:"flex-end"}}>
                <textarea aria-label="WhatsApp reply" placeholder="Type a WhatsApp message…" value={replyText} onChange={e=>setReplyText(e.target.value)} disabled={sendingReply} rows={2} style={{flex:1,minWidth:0,padding:10,border:"1px solid #cbd5e1",borderRadius:8,fontFamily:"inherit",resize:"vertical"}} />
                <button onClick={sendWhatsAppReply} disabled={sendingReply || !replyText.trim()} style={{background:"#128c7e",color:"white",border:0,borderRadius:8,padding:"12px 14px",cursor:"pointer",opacity:sendingReply||!replyText.trim()?0.5:1}}>{sendingReply?"Sending…":"Send"}</button>
              </div>
            </>
          ) : <div style={{margin:"auto",padding:30,textAlign:"center",color:"#64748b"}}>Select a customer to view their WhatsApp conversation.</div>}
        </section>

        <section className="mukbi-panel mukbi-details" aria-label="Customer details and notes">
          {selectedTimelineLead ? (()=>{
            const lead=leads.find(l=>l._id===selectedTimelineLead._id)||selectedTimelineLead;
            const notes=(lead.notes||[]).filter(n=>n.type!=="WhatsApp" && n.type!=="System");
            const activities=(lead.notes||[]).filter(n=>n.type==="System");
            const isLost=lead.status==="Lost";
            return <div style={{padding:16,overflowY:"auto",maxHeight:700}}>
              <h3 style={{margin:"0 0 12px",color:"#0f172a"}}>Customer Details</h3>
              <label style={{fontSize:12,color:"#64748b"}}>Name</label>
              <input aria-label="Customer name" disabled={isLost} value={localNames[lead._id] ?? lead.name ?? ""} onChange={e=>setLocalNames(prev=>({...prev,[lead._id]:e.target.value}))} onBlur={e=>{if(e.target.value!==lead.name)updateLeadField(lead._id,"name",e.target.value);}} style={{width:"100%",boxSizing:"border-box",padding:8,margin:"4px 0 10px",border:"1px solid #cbd5e1",borderRadius:6}} />
              <div style={{fontSize:13,color:"#475569",marginBottom:10}}>Phone: {lead.phone || "—"}</div>
              <div style={{fontSize:13,color:"#475569",marginBottom:12}}>Requirement: {lead.requirement || "—"}</div>
              <label style={{fontSize:12,color:"#64748b"}}>Status</label>
              <select aria-label="Customer status" value={lead.status || "New"} onChange={e=>updateStatus(lead._id,e.target.value)} style={{width:"100%",padding:8,margin:"4px 0 10px",border:"1px solid #cbd5e1",borderRadius:6}}>
                {["New","Imported","In Progress","Won","Lost"].map(status=><option key={status} value={status}>{status}</option>)}
              </select>
              <label style={{fontSize:12,color:"#64748b"}}>Next follow-up</label>
              <input aria-label="Next follow-up" type="date" disabled={isLost} value={lead.next_followup_date || ""} onChange={e=>updateFollowup(lead._id,e.target.value)} style={{width:"100%",boxSizing:"border-box",padding:8,margin:"4px 0 14px",border:"1px solid #cbd5e1",borderRadius:6}} />
              <div style={{borderTop:"1px solid #e2e8f0",paddingTop:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <strong>CRM Notes</strong>
                <button disabled={isLost} onClick={()=>{setSelectedLeadForNote(lead);setNoteTypeInputs(prev=>({...prev,[lead._id]:prev[lead._id]==="WhatsApp"?"User":(prev[lead._id]||"User")}));setShowAddNoteModal(true);}} style={{background:"#2563eb",color:"white",border:0,borderRadius:6,padding:"7px 10px",cursor:"pointer"}}>+ Add Note</button>
              </div>
              {notes.length===0 && <p style={{fontSize:13,color:"#94a3b8"}}>No CRM notes yet.</p>}
              {[...notes].reverse().map((n,i)=><div key={n._id||i} style={{padding:"10px 0",borderBottom:"1px solid #f1f5f9"}}><div style={{fontSize:12,fontWeight:700,color:"#334155"}}>{n.type==="User"?"General":n.type||"General"}</div><div style={{fontSize:13,whiteSpace:"pre-wrap",marginTop:4}}>{n.text}</div><div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>{n.date&&!Number.isNaN(new Date(n.date).getTime())?new Date(n.date).toLocaleString("en-IN"):""}</div></div>)}
              <h4 style={{borderTop:"1px solid #e2e8f0",paddingTop:14,marginBottom:8}}>System Activities</h4>
              {activities.length===0 && <p style={{fontSize:13,color:"#94a3b8"}}>No recorded system activities.</p>}
              {[...activities].reverse().map((n,i)=><div key={n._id||i} style={{padding:"8px 0",fontSize:13,borderBottom:"1px solid #f1f5f9"}}>{n.text}</div>)}
            </div>;
          })() : <div style={{padding:24,color:"#64748b"}}>Customer details and CRM notes will appear here.</div>}
        </section>
      </div>

      {showAddNoteModal && selectedLeadForNote && (
        <div
          onClick={() => setShowAddNoteModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              width: "90%",
              maxWidth: "500px",
              padding: "24px",
              borderRadius: "12px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              position: "relative"
            }}
          >
            {/* Close */}
            <button
              onClick={() => setShowAddNoteModal(false)}
              style={{
                position: "absolute",
                top: "10px",
                right: "12px",
                border: "none",
                background: "transparent",
                fontSize: "22px",
                color: "#6b7280",
                cursor: "pointer"
              }}
              aria-label="Close"
            >
              ×
            </button>

            <h3
              style={{
                marginTop: 0,
                marginBottom: "4px",
                color: "#1f2937"
              }}
            >
              Add Note
            </h3>

            <div
              style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "18px"
              }}
            >
              {selectedLeadForNote.name}
            </div>

            {/* Note Type */}
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "6px",
                color: "#374151"
              }}
            >
              Type
            </label>

            <select
              value={
                noteTypeInputs[selectedLeadForNote._id] || "User"
              }
              onChange={(e) =>
                setNoteTypeInputs(prev => ({
                  ...prev,
                  [selectedLeadForNote._id]: e.target.value
                }))
              }
              style={{
                width: "100%",
                padding: "9px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                boxSizing: "border-box",
                marginBottom: "14px"
              }}
            >
              <option value="User">User</option>
              <option value="Call">Call</option>
              <option value="Visit">Visit</option>
            </select>

            {/* Note */}
            <label
              style={{
                display: "block",
                fontSize: "13px",
                fontWeight: "600",
                marginBottom: "6px",
                color: "#374151"
              }}
            >
              Note
            </label>

            <textarea
              autoFocus
              placeholder="Enter your note..."
              value={noteInputs[selectedLeadForNote._id] || ""}
              onChange={(e) =>
                setNoteInputs(prev => ({
                  ...prev,
                  [selectedLeadForNote._id]: e.target.value
                }))
              }
              rows={5}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                resize: "vertical",
                boxSizing: "border-box",
                fontFamily: "inherit",
                fontSize: "14px"
              }}
            />

            {/* Add */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "15px"
              }}
            >
              <button
                onClick={() => {
                  addNote(selectedLeadForNote._id);
                  setShowAddNoteModal(false);
                  setSelectedLeadForNote(null);
                }}
                style={{
                  padding: "8px 16px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600"
                }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Leads;